import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Building2, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";

export default function EnterpriseContactModal({ open, onClose }) {
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        phone: "",
        use_case: "",
        expected_volume: "",
    });

    const submit = async () => {
        if (!form.name || !form.email || !form.company) {
            toast.error("Nome, email e azienda sono obbligatori");
            return;
        }
        setBusy(true);
        try {
            await api.enterpriseContact(form);
            setDone(true);
        } catch (e) {
            toast.error(e.message || "Errore invio richiesta");
        } finally {
            setBusy(false);
        }
    };

    const reset = () => {
        setDone(false);
        setForm({
            name: "",
            email: "",
            company: "",
            phone: "",
            use_case: "",
            expected_volume: "",
        });
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => {
                        if (!busy) {
                            onClose();
                            setTimeout(reset, 200);
                        }
                    }}
                    data-testid="enterprise-modal"
                >
                    <motion.div
                        initial={{ scale: 0.96, y: 12, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.96, y: 12, opacity: 0 }}
                        className="relative w-full max-w-lg rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            data-testid="enterprise-close"
                            disabled={busy}
                            className="absolute top-3 right-3 p-1 text-text-secondary hover:text-text-main"
                            onClick={() => {
                                onClose();
                                setTimeout(reset, 200);
                            }}
                        >
                            <X className="h-5 w-5" />
                        </button>
                        {done ? (
                            <div className="text-center py-4">
                                <CheckCircle2 className="h-10 w-10 mx-auto text-[var(--color-success)]" />
                                <h3 className="mt-3 text-lg font-semibold">
                                    Grazie, {form.name.split(" ")[0]}!
                                </h3>
                                <p className="mt-1 text-sm text-text-secondary">
                                    Ti contattiamo entro 24h all'indirizzo{" "}
                                    <strong className="text-text-main">{form.email}</strong>.
                                </p>
                                <button
                                    onClick={() => {
                                        onClose();
                                        setTimeout(reset, 200);
                                    }}
                                    className="mt-6 rounded-full border border-[var(--color-border)] px-4 py-2 text-sm hover:border-[var(--color-primary)]/40"
                                >
                                    Chiudi
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-start gap-3">
                                    <div className="h-9 w-9 rounded-full bg-[var(--color-primary)]/15 flex items-center justify-center text-[var(--color-primary)]">
                                        <Building2 className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold">Enterprise</h3>
                                        <p className="text-xs text-text-secondary">
                                            SLA, SSO, volume custom. Ti contattiamo entro 24h.
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-5 space-y-3">
                                    <Field
                                        label="Nome completo *"
                                        testid="ent-name"
                                        value={form.name}
                                        onChange={(v) => setForm((f) => ({ ...f, name: v }))}
                                    />
                                    <div className="grid grid-cols-2 gap-3">
                                        <Field
                                            label="Email business *"
                                            testid="ent-email"
                                            type="email"
                                            value={form.email}
                                            onChange={(v) =>
                                                setForm((f) => ({ ...f, email: v }))
                                            }
                                        />
                                        <Field
                                            label="Azienda *"
                                            testid="ent-company"
                                            value={form.company}
                                            onChange={(v) =>
                                                setForm((f) => ({ ...f, company: v }))
                                            }
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <Field
                                            label="Telefono"
                                            testid="ent-phone"
                                            value={form.phone}
                                            onChange={(v) =>
                                                setForm((f) => ({ ...f, phone: v }))
                                            }
                                        />
                                        <Field
                                            label="Volume atteso"
                                            testid="ent-volume"
                                            placeholder="es. 50k lead/mese"
                                            value={form.expected_volume}
                                            onChange={(v) =>
                                                setForm((f) => ({ ...f, expected_volume: v }))
                                            }
                                        />
                                    </div>
                                    <Field
                                        label="Use case"
                                        testid="ent-usecase"
                                        multiline
                                        value={form.use_case}
                                        onChange={(v) =>
                                            setForm((f) => ({ ...f, use_case: v }))
                                        }
                                    />
                                </div>
                                <div className="mt-6 flex justify-end gap-2">
                                    <button
                                        data-testid="enterprise-submit"
                                        onClick={submit}
                                        disabled={busy}
                                        className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-[#EDEDED] px-5 py-2 text-sm font-medium text-black hover:opacity-90 disabled:opacity-50"
                                    >
                                        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                        Invia richiesta
                                    </button>
                                </div>
                            </>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function Field({ label, testid, type, multiline, value, onChange, placeholder }) {
    const cls =
        "w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--color-primary)]/60";
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-text-secondary">
                {label}
            </span>
            {multiline ? (
                <textarea
                    data-testid={testid}
                    value={value}
                    placeholder={placeholder}
                    rows={3}
                    onChange={(e) => onChange(e.target.value)}
                    className={`mt-1 ${cls}`}
                />
            ) : (
                <input
                    data-testid={testid}
                    type={type || "text"}
                    value={value}
                    placeholder={placeholder}
                    onChange={(e) => onChange(e.target.value)}
                    className={`mt-1 ${cls}`}
                />
            )}
        </label>
    );
}
